// Asset Transactions booking screen.
//
// Type-driven dynamic form (spec: "Asset Transaction form V1.0"): the common
// header fields (name / date / late booking / deal / type / company) stay
// fixed on the left, and the right column shows only the fields the selected
// Type uses. Visibility is entirely cfg-driven from the type's pattern flags
// delivered via globalConfigs.asset_transaction_types (tranche_created /
// targets_existing / has_scope / uses_price / uses_split_factor /
// uses_units_disposed / escrow_column / amount_label / has_additional_payment
// / exercisable_only / uses_interest_capitalization / uses_interest_period_end)
// plus cfg.asset_valuation_types.is_countable for the Number-vs-Principal
// split — no per-type logic is hardcoded here.
//
// Accrued interest (docs/INTEREST_ACCRUAL_DESIGN.md sections 6.2, 6.3; PR B):
// a transaction targeting a tranche whose security carries interest terms
// gets an "Accrued interest" section fed by the detail payload's `interest`
// block - the engine's calculated accrual, the booked balance and the
// interest earned before the transaction. An accrual (uses_interest_period_end)
// shows "This accrual" = calculated - earned and flags an amount that differs
// as an override; a conversion (uses_interest_capitalization) shows the slice's
// booked balance, the calculated figure, the Override input
// (interest_capitalized_override) and what it capitalises with its true-up;
// an exit shows the open balance and offers the receipt and the write-off,
// each a new transaction one minute before this one.
//
// Draft lifecycle: every save round-trips through POST /asset-transactions
// (at__save_transaction), which regenerates the default journal preview
// server-side (or preserves the user-customized set) and returns the full
// detail. The journal grid is FULLY editable — edit / add / delete lines —
// with a "Reset to default" action that regenerates the engine defaults.
// Booking (at__book_transaction_checked) preserves the stored journal set;
// Booked transactions render read-only with Trash as the only action (no
// unbook in v1 — corrections go through Trash + re-entry).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd'
import DatePicker from '../../../../core/components/DatePicker.tsx'
import { DeleteOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import DisabledReason from '../../../../core/components/DisabledReason.tsx'
import { useCapability } from '../../../../core/hooks/useCapability.ts'
import { TABLE_ASSET_TRANSACTIONS, TABLE_DISTRIBUTIONS } from '../../../../state_manager/globals.ts'
import type {
  AssetTransactionDetail,
  AssetTransactionSavePayload,
  AssetTransactionCompanyOptions,
  AssetTransactionHeaderOptions,
  AssetTransactionSecurityOption,
  AssetTransactionTrancheOption,
  ValuationScopeCurrency
} from '../../../../state_manager/data_models/asset_transaction_detail.ts'
import {
  fetchAssetTransactionDetail,
  fetchAssetTransactionHeaderOptions,
  fetchAssetTransactionCompanyOptions,
  fetchValuationScopeCurrencies,
  saveAssetTransaction,
  resetAssetTransactionJournals,
  bookAssetTransaction,
  unbookAssetTransaction,
  trashAssetTransaction
} from '../../services/assetTransactionsService.ts'
import {
  updateAssetTransactionRecord,
  removeAssetTransaction
} from '../../../../state_manager/state_update_actions/asset_transactions/index.ts'
import { renderTrancheOption, trancheOptionFilter } from '../trancheOptionRender.tsx'
import {
  renderSecurityOption,
  securityOptionFilter,
  securityTermsSummary
} from '../securityOptionRender.tsx'
import AddSecurityModal from '../AddSecurityModal.tsx'
import { useNumberFormat, useNumberDisplay, decimalToPercent, percentToDecimal } from '../../../../core/utils/inputNumberFormat.ts'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import ExportRowsButton from '../../../shared/table_grid_export/ExportRowsButton.tsx'

const { Title, Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
// Transaction date carries a TIME: same-day sequence matters to the engine
// (as-of values use strictly-before timestamps — e.g. a SAFE conversion must
// precede the same-day valuation of the resulting stock). Serialized without
// a zone offset (wall-clock) so the entered time round-trips verbatim.
const DATETIME_FORMAT = 'MM/DD/YYYY HH:mm'
const DATETIME_VALUE = 'YYYY-MM-DDTHH:mm:ss'
const LIST_PATH = '/asset-transactions'
// Status label the at__ engine treats as immutable (matches the RPC guards).
const STATUS_LABEL_BOOKED = 'Booked'

interface FullAssetTransactionFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface JournalRow {
  key: string
  id?: string
  acc_item_id: string | null
  debit_credit_id: 'dr' | 'cr'
  amount: number | null
  description: string
  tranche_id: string | null
  tranche_name?: string | null
}

let rowKeySeq = 0
const nextRowKey = () => `row-${++rowKeySeq}`

export default function FullAssetTransactionForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullAssetTransactionFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { openFormInTab, closeFormTab } = useTabManagerContext()
  const [modal, modalContextHolder] = Modal.useModal()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  // New-record tabs are keyed per prefill query (see tabSlice.addFormTab), so
  // the key must carry the mount-time search string. Snapshot it: the global
  // location later reflects whichever tab is active, not this one.
  const [mountSearch] = useState(() => (isNew ? location.search : ''))
  const formTabKey = `asset-transactions-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}${mountSearch}`

  // Prefill for new records, read once from the query string (set by
  // openFormInTab callers, e.g. "+ Transaction" on a deal's Execution tab).
  const [prefill] = useState(() => {
    if (!isNew) return { deal: null, company: null, type_id: null, tranche: null, date: null }
    const params = new URLSearchParams(location.search)
    const typeParam = params.get('type_id')
    return {
      deal: params.get('deal'),
      company: params.get('company'),
      // the "Receive in cash..." / "Write off..." actions of the Accrued
      // interest section open the new form on a type, a tranche and a date
      type_id: typeParam ? Number(typeParam) : null,
      tranche: params.get('tranche'),
      date: params.get('date')
    }
  })

  // cfg-driven decimal display (cfg.number_formats): currency pads to 2,
  // unit prices to 8, fx rates to 8 (max 10); unit counts show decimals only
  // when present
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')
  const unitCountFmt = useNumberFormat('unit_count')
  const unitPriceFmt = useNumberFormat('unit_price')
  const fxRateFmt = useNumberFormat('fx_rate')

  const assetTypes = useSelector((state: any) => state.globalConfigs?.asset_transaction_types ?? [])
  const atTreatments = useSelector((state: any) => state.globalConfigs?.at_accounting_treatments ?? [])
  const scopes = useSelector((state: any) => state.globalConfigs?.asset_transaction_scopes ?? [])
  const currencies = useSelector((state: any) => state.globalConfigs?.currencies ?? [])
  const funds = useSelector((state: any) => state.globalConfigs?.funds ?? [])
  const accItems = useSelector((state: any) => state.globalConfigs?.acc_items ?? [])

  const [detail, setDetail] = useState<AssetTransactionDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [journalsDirty, setJournalsDirty] = useState(false)
  const [activeTab, setActiveTab] = useState('details')

  // Common header fields
  const [name, setName] = useState('')
  // New bookings default to 08:00 (owner spec): a stable morning anchor that
  // leaves room to sequence same-day transactions before/after it.
  const [transactionDate, setTransactionDate] = useState<string | null>(
    prefill.date ?? dayjs().format('YYYY-MM-DD') + 'T08:00:00'
  )
  const [lateBooking, setLateBooking] = useState(false)
  const [firstReportDate, setFirstReportDate] = useState<string | null>(null)
  const [dealId, setDealId] = useState<string | null>(prefill.deal)
  const [typeId, setTypeId] = useState<number | null>(prefill.type_id)
  const [companyId, setCompanyId] = useState<string | null>(prefill.company)

  // Type-specific fields
  const [warehoused, setWarehoused] = useState(false)
  // a transfer between funds: the sending fund's Disposal this acquisition
  // took a warehoused position over from (null = an ordinary acquisition)
  const [transferFromTransaction, setTransferFromTransaction] = useState<string | null>(null)
  const [allFunds, setAllFunds] = useState(true)
  const [fundId, setFundId] = useState<string | null>(null)
  const [scopeId, setScopeId] = useState<number | null>(null)
  const [valuationTypeId, setValuationTypeId] = useState<number | null>(null)
  const [securityTypeId, setSecurityTypeId] = useState<number | null>(null)
  const [securityId, setSecurityId] = useState<string | null>(null)
  const [newSecurityId, setNewSecurityId] = useState<string | null>(null)
  const [trancheId, setTrancheId] = useState<string | null>(prefill.tranche)
  const [currencyId, setCurrencyId] = useState<string | null>(null)
  const [amount, setAmount] = useState<number | null>(null)
  const [amountFunctional, setAmountFunctional] = useState<number | null>(null)
  const [noAcquired, setNoAcquired] = useState<number | null>(null)
  const [principalAcquired, setPrincipalAcquired] = useState<number | null>(null)
  const [noDisposed, setNoDisposed] = useState<number | null>(null)
  const [principalDisposed, setPrincipalDisposed] = useState<number | null>(null)
  const [splitFactor, setSplitFactor] = useState<number | null>(null)
  // Securities distributions: % of the distributing tranche's cost basis
  // allocated to the received tranche (displayed as %, stored as decimal)
  const [percCostAllocated, setPercCostAllocated] = useState<number | null>(null)
  // Accounting treatment (cfg.at_accounting_treatments) — decides which
  // journal/delta rules fire and which value input shows
  const [treatmentId, setTreatmentId] = useState<number | null>(null)
  // A conversion under a Fair value treatment (uses_fair_value): the total
  // fair value of the securities received, and its functional twin
  // (asset-delta design PR 2, 2026-10-21)
  const [fairValue, setFairValue] = useState<number | null>(null)
  const [fairValueFunctional, setFairValueFunctional] = useState<number | null>(null)
  const [price, setPrice] = useState<number | null>(null)
  const [priceFunctional, setPriceFunctional] = useState<number | null>(null)
  // Valuation multi-currency unit values: functional currencies of in-scope
  // holdings (polled) + entered values keyed by currency id.
  const [valuationCurrencies, setValuationCurrencies] = useState<ValuationScopeCurrency[]>([])
  const [valuationValues, setValuationValues] = useState<Record<string, number | null>>({})
  const [escrowDeclared, setEscrowDeclared] = useState<number | null>(null)
  const [escrowDeclaredFunctional, setEscrowDeclaredFunctional] = useState<number | null>(null)
  const [escrowPaid, setEscrowPaid] = useState<number | null>(null)
  const [escrowPaidFunctional, setEscrowPaidFunctional] = useState<number | null>(null)
  const [escrowReduced, setEscrowReduced] = useState<number | null>(null)
  const [escrowReducedFunctional, setEscrowReducedFunctional] = useState<number | null>(null)
  const [additionalPayment, setAdditionalPayment] = useState(false)
  // interest: a conversion's capitalised-interest override (security
  // currency; null = the engine's figure) and an accrual's period end
  const [interestOverride, setInterestOverride] = useState<number | null>(null)
  const [interestPeriodEnd, setInterestPeriodEnd] = useState<string | null>(null)
  const [fxRate, setFxRate] = useState<number | null>(null)
  const [notes, setNotes] = useState('')

  // Journal lines (fully editable local state)
  const [journals, setJournals] = useState<JournalRow[]>([])

  // Option feeds
  const [headerOptions, setHeaderOptions] = useState<AssetTransactionHeaderOptions | null>(null)
  const [companyOptions, setCompanyOptions] = useState<AssetTransactionCompanyOptions | null>(null)
  // which security select launched the "+ New security" modal
  const [addSecurityFor, setAddSecurityFor] = useState<'security' | 'new_security' | null>(null)

  const isBooked = detail?.transaction?.status_label === STATUS_LABEL_BOOKED
  const readOnly = isBooked

  // User rights (docs/USER_RIGHTS_DESIGN.md 6.2): a control the user may not
  // use is disabled with the database's sentence; the database refuses
  // whatever the screen shows.
  const createRight = useCapability('asset_transactions.create')
  const editRight = useCapability('asset_transactions.edit')
  const bookRight = useCapability('asset_transactions.book')
  const unbookRight = useCapability('asset_transactions.unbook')
  const deleteRight = useCapability('asset_transactions.delete')

  const selectedType = useMemo(() => assetTypes.find((t: any) => t.id === typeId) ?? null, [assetTypes, typeId])
  const journalsOnSeparateTab = Boolean(selectedType?.journals_separate_tab)

  // ----- cfg-driven visibility -----
  const scopeTarget = useMemo(
    () => scopes.find((s: any) => s.id === scopeId)?.target_field ?? null,
    [scopes, scopeId]
  )
  const showWarehoused = Boolean(selectedType?.tranche_created && !selectedType?.targets_existing)
  const showAllFunds = Boolean(selectedType?.has_scope)
  const showFund = Boolean(
    (selectedType?.tranche_created && !selectedType?.targets_existing) ||
      (selectedType?.has_scope && !allFunds)
  )
  const showScope = Boolean(selectedType?.has_scope)
  const showSecuritySelect = Boolean(
    (selectedType?.tranche_created && !selectedType?.targets_existing) ||
      (selectedType?.has_scope && scopeTarget === 'security')
  )
  const showTrancheSelect = Boolean(
    selectedType?.tranche_targeted || (selectedType?.has_scope && scopeTarget === 'tranche_targeted')
  )
  const showValuationTypeSelect = Boolean(selectedType?.has_scope && scopeTarget === 'valuation_type_id')
  const showSecurityTypeSelect = Boolean(selectedType?.has_scope && scopeTarget === 'security_type_id')
  const showNewSecurity = Boolean(selectedType?.tranche_created && selectedType?.targets_existing)
  const showAdditionalPayment = Boolean(selectedType?.has_additional_payment)
  const showAmountPair = Boolean(
    selectedType?.amount_label && (!selectedType?.has_additional_payment || additionalPayment)
  )
  const showSplitFactor = Boolean(selectedType?.uses_split_factor)
  const showInterestOverride = Boolean(selectedType?.uses_interest_capitalization)
  const showInterestPeriodEnd = Boolean(selectedType?.uses_interest_period_end)
  const showPricePair = Boolean(selectedType?.uses_price)
  const showDisposed = Boolean(selectedType?.uses_units_disposed)
  // Accounting treatments: when the TYPE defines them (a treatment row of
  // the pattern whose asset_transaction_type_id is NULL - every type of the
  // pattern - or this type), the CHOSEN treatment decides which value input
  // shows (and which rules fire). A value input is gated by the treatment
  // only where some treatment of the type uses it: the Conversion's pair
  // uses neither amount nor percent, so the additional payment stays the
  // type's own.
  const patternTreatments = useMemo(
    () =>
      atTreatments
        .filter(
          (t: any) =>
            t.pattern_id === selectedType?.pattern_id &&
            (t.asset_transaction_type_id == null || t.asset_transaction_type_id === selectedType?.id)
        )
        .sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [atTreatments, selectedType?.pattern_id, selectedType?.id]
  )
  const hasTreatments = patternTreatments.length > 0
  const treatmentsUseAmount = patternTreatments.some((t: any) => t.uses_amount)
  const treatmentsUsePercent = patternTreatments.some((t: any) => t.uses_percent)
  const selectedTreatment = useMemo(
    () => patternTreatments.find((t: any) => t.id === treatmentId) ?? null,
    [patternTreatments, treatmentId]
  )
  const showCostAllocation =
    hasTreatments && treatmentsUsePercent
      ? Boolean(selectedTreatment?.uses_percent)
      : Boolean(selectedType?.uses_cost_allocation)
  const showAmountInput =
    showAmountPair && (!hasTreatments || !treatmentsUseAmount || Boolean(selectedTreatment?.uses_amount))
  // the Fair value (taxable exchange) election: the fair value received pair
  const showFairValue = Boolean(selectedTreatment?.uses_fair_value)
  const escrowColumn: string | null = selectedType?.escrow_column ?? null
  const showCurrency = showAmountPair || showPricePair || Boolean(escrowColumn)

  // ----- option lists -----
  const valuationTypesCfg = useSelector((state: any) => state.globalConfigs?.asset_valuation_types ?? [])
  const typeOptions = useMemo(
    () => assetTypes.map((t: any) => ({ value: t.id, label: t.label })),
    [assetTypes]
  )
  const scopeOptions = useMemo(
    () => scopes.map((s: any) => ({ value: s.id, label: s.label })),
    [scopes]
  )
  const securityTypesCfg = useSelector((state: any) => state.globalConfigs?.security_types ?? [])
  const securityTypeOptions = useMemo(
    () => securityTypesCfg.map((t: any) => ({ value: t.id, label: t.label })),
    [securityTypesCfg]
  )
  const valuationTypeOptions = useMemo(
    () => valuationTypesCfg.map((v: any) => ({ value: v.id, label: v.label })),
    [valuationTypesCfg]
  )
  const currencyOptions = useMemo(
    () => currencies.map((c: any) => ({ value: c.id, label: c.code })),
    [currencies]
  )
  const accItemOptions = useMemo(
    () => accItems.map((i: any) => ({ value: i.id, label: i.label })),
    [accItems]
  )
  const companySelectOptions = headerOptions?.companies ?? []
  // Deal select lists every deal: a transaction may be booked on another
  // company than its deal's (an acquisition booked on the acquirer, a share
  // distribution received from a portfolio company). The selected company's
  // deals come first; another company's deal carries that company's name, so
  // typing the company finds it.
  const dealSelectOptions = useMemo(() => {
    const all = headerOptions?.deals ?? []
    if (!companyId) return all
    const companyNames = new Map(companySelectOptions.map((c) => [c.value, c.label]))
    const own = all.filter((d) => d.account_id === companyId)
    const others = all
      .filter((d) => d.account_id !== companyId)
      .map((d) => {
        const company = d.account_id ? companyNames.get(d.account_id) : null
        return company ? { ...d, label: `${d.label} (${company})` } : d
      })
    return [...own, ...others]
  }, [headerOptions, companySelectOptions, companyId])
  const securityOptions = companyOptions?.securities ?? []
  const allSecurityOptions = companyOptions?.all_securities ?? []
  // "Transferred from": the Disposals on the company's warehoused tranches in
  // OTHER funds (a transfer moves a position between two funds); the one
  // already linked stays listed whatever fund the form shows
  const transferSourceOptions = useMemo(
    () =>
      (companyOptions?.transfer_sources ?? []).filter(
        (s) => s.value === transferFromTransaction || !fundId || s.funds__id !== fundId
      ),
    [companyOptions, fundId, transferFromTransaction]
  )
  const trancheOptions = useMemo(() => {
    const all = companyOptions?.tranches ?? []
    return selectedType?.exercisable_only ? all.filter((t) => t.is_exercisable) : all
  }, [companyOptions, selectedType])

  const selectedDealOption = useMemo(
    () => (headerOptions?.deals ?? []).find((d) => d.value === dealId) ?? null,
    [headerOptions, dealId]
  )
  // New-tranche bookings without a security auto-create one server-side from
  // the deal's round (type + terms) — surface that instead of looking broken.
  // Not for another company's deal: its round is that company's instrument,
  // so at__ensure_security_from_round skips and the security must be chosen.
  const securityAutoCreateLabel =
    selectedType?.tranche_created &&
    !selectedType?.targets_existing &&
    !securityId &&
    selectedDealOption?.account_id === companyId &&
    selectedDealOption?.round_security_type__label
      ? selectedDealOption.round_security_type__label
      : null

  const selectedSecurity: AssetTransactionSecurityOption | null = useMemo(
    () => securityOptions.find((s) => s.value === securityId) ?? null,
    [securityOptions, securityId]
  )
  const selectedNewSecurity: AssetTransactionSecurityOption | null = useMemo(
    () => allSecurityOptions.find((s) => s.value === newSecurityId) ?? null,
    [allSecurityOptions, newSecurityId]
  )
  const selectedTranche: AssetTransactionTrancheOption | null = useMemo(
    () => (companyOptions?.tranches ?? []).find((t) => t.value === trancheId) ?? null,
    [companyOptions, trancheId]
  )

  // Fund context: header fund, else the targeted tranche's fund.
  const effectiveFundId = fundId ?? selectedTranche?.funds__id ?? null
  const functionalCurrencyId = useMemo(() => {
    if (!effectiveFundId) return null
    return funds.find((f: any) => f.id === effectiveFundId)?.functional_currency_id ?? null
  }, [funds, effectiveFundId])

  const currencySymbol = (id: string | null): string | undefined =>
    currencies.find((c: any) => c.id === id)?.symbol

  const showFunctionalTwin = Boolean(currencyId && functionalCurrencyId && currencyId !== functionalCurrencyId)

  // Acquired fields: the security being received (new_security for
  // conversion-shaped types, the subscribed security otherwise). Its
  // valuation-type countability decides Number vs Principal.
  const acquiredSecurity = showNewSecurity ? selectedNewSecurity : selectedSecurity
  const showAcquired = Boolean(selectedType?.tranche_created)
  const acquiredCountable = acquiredSecurity?.is_countable ?? true
  const acquiredNoun = selectedType?.targets_existing ? 'received' : 'acquired'
  const disposedCountable = selectedTranche?.is_countable ?? true
  const disposedNoun = selectedType?.exercisable_only
    ? 'exercised'
    : selectedType?.tranche_created && selectedType?.targets_existing
      ? 'converting'
      : 'disposed'

  // at__save_transaction filled the stored rate from the FX feed (fx_rates):
  // use_fx_rate without specify_fx_rate, and the rate on screen is still it
  const fxRateFromFeed =
    detail?.transaction?.use_fx_rate === true &&
    detail.transaction.specify_fx_rate === false &&
    detail.transaction.fx_rate != null &&
    fxRate === detail.transaction.fx_rate
  // shown under each functional twin the feed filled - the FX rate field
  // itself hides once both amounts are set, so the twin is where it is seen
  const feedHint = fxRateFromFeed ? (
    <div className='text-xs text-gray-400 mt-1'>Filled from the FX feed at {detail?.transaction?.fx_rate}</div>
  ) : null

  // fx_rate visibility (spec predicates, computed from currency ids)
  const showFxRate = useMemo(() => {
    if (!functionalCurrencyId) return false
    if (showNewSecurity) {
      const nsCcy = selectedNewSecurity?.currency_id ?? null
      if (!nsCcy || nsCcy === functionalCurrencyId) return false
      if (selectedType?.exercisable_only || selectedType?.has_additional_payment) {
        return currencyId !== nsCcy || !amount || !amountFunctional
      }
      return true
    }
    if (showAcquired) {
      const sCcy = selectedSecurity?.currency_id ?? null
      if (!sCcy || sCcy === functionalCurrencyId) return false
      return !amount || !amountFunctional || currencyId !== sCcy
    }
    return false
  }, [
    functionalCurrencyId,
    showNewSecurity,
    showAcquired,
    selectedNewSecurity,
    selectedSecurity,
    selectedType,
    currencyId,
    amount,
    amountFunctional
  ])

  const hydrateFromDetail = useCallback((d: AssetTransactionDetail) => {
    const t = d.transaction
    setDetail(d)
    setName(t.name ?? '')
    // keep the stored wall-clock time verbatim (slice strips the zone offset
    // so dayjs doesn't shift it into the browser's timezone)
    setTransactionDate(t.transaction_date ? t.transaction_date.slice(0, 19) : null)
    setLateBooking(Boolean(t.late_booking))
    setFirstReportDate(t.first_report_date ?? null)
    setDealId(t.deal ?? null)
    setTypeId(t.type_id)
    setCompanyId(t.company)
    setWarehoused(Boolean(t.warehoused))
    setTransferFromTransaction(t.transfer_from_transaction ?? null)
    setAllFunds(t.all_funds ?? true)
    setFundId(t.fund ?? null)
    setScopeId(t.scope_id ?? null)
    setValuationTypeId(t.valuation_type_id ?? null)
    setSecurityTypeId(t.security_type_id ?? null)
    setSecurityId(t.security ?? null)
    setNewSecurityId(t.new_security ?? null)
    setTrancheId(t.tranche_targeted ?? null)
    setCurrencyId(t.currency_id ?? null)
    setAmount(t.amount ?? null)
    setAmountFunctional(t.amount_functional ?? null)
    setNoAcquired(t.no_acquired ?? null)
    setPrincipalAcquired(t.principal_acquired ?? null)
    setNoDisposed(t.no_disposed ?? null)
    setPrincipalDisposed(t.principal_disposed ?? null)
    setSplitFactor(t.split_factor ?? null)
    setPercCostAllocated(decimalToPercent(t.perc_cost_allocated))
    setTreatmentId(t.accounting_treatment_id ?? null)
    setFairValue(t.fair_value ?? null)
    setFairValueFunctional(t.fair_value_functional ?? null)
    setPrice(t.price ?? null)
    setPriceFunctional(t.price_functional ?? null)
    setEscrowDeclared(t.escrow_declared ?? null)
    setEscrowDeclaredFunctional(t.escrow_declared_functional ?? null)
    setEscrowPaid(t.escrow_paid ?? null)
    setEscrowPaidFunctional(t.escrow_paid_functional ?? null)
    setEscrowReduced(t.escrow_reduced ?? null)
    setEscrowReducedFunctional(t.escrow_reduced_functional ?? null)
    setAdditionalPayment(Boolean(t.additional_payment))
    setInterestOverride(t.interest_capitalized_override ?? null)
    setInterestPeriodEnd(t.interest_period_end ?? null)
    setFxRate(t.fx_rate ?? null)
    setNotes(t.notes ?? '')
    setJournals(
      d.journals.map((j) => ({
        key: j.id,
        id: j.id,
        acc_item_id: j.item_id ?? null,
        debit_credit_id: (j.debit_credit_id === 'cr' ? 'cr' : 'dr') as 'dr' | 'cr',
        amount: j.amount,
        description: j.description ?? '',
        tranche_id: j.tranche_id ?? null,
        tranche_name: j.tranche_name ?? null
      }))
    )
    setValuationValues(
      Object.fromEntries(
        (d.valuation_values ?? []).map((v) => [v.currency_functional_id, v.unit_value_functional])
      )
    )
    setDirty(false)
    setJournalsDirty(false)
  }, [])

  // Header options once on mount.
  useEffect(() => {
    let cancelled = false
    const token = getAccessToken(store.getState().auth)
    ;(async () => {
      const res = await fetchAssetTransactionHeaderOptions(token)
      if (!cancelled && res.data) setHeaderOptions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Valuation scope poll: which functional currencies do the in-scope
  // holdings carry as-of the transaction date? Re-runs on any scope/date
  // change; the entered values keep their keys, so a scope change never
  // wipes an entry — it just stops being sent if no longer required.
  useEffect(() => {
    if (!showPricePair || !companyId) {
      setValuationCurrencies([])
      return
    }
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchValuationScopeCurrencies(
        companyId,
        {
          asOf: transactionDate,
          fundId: !allFunds && fundId ? fundId : null,
          valuationTypeId: scopeTarget === 'valuation_type_id' ? valuationTypeId : null,
          securityTypeId: scopeTarget === 'security_type_id' ? securityTypeId : null,
          securityId: scopeTarget === 'security' ? securityId : null,
          trancheId: scopeTarget === 'tranche_targeted' ? trancheId : null
        },
        token
      )
      if (!cancelled && res.data) setValuationCurrencies(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [showPricePair, companyId, transactionDate, allFunds, fundId, scopeTarget, valuationTypeId, securityTypeId, securityId, trancheId])

  // The base currency (currencies.is_base) is the firm default: it stands in
  // as the currency for valuation-shaped types (uses_price), which have no
  // acquiring/converting security to inherit one from, and as the functional
  // currency for a valuation booked without a fund.
  const baseCurrencyId = useMemo(
    () => currencies.find((c: any) => c.is_base)?.id ?? null,
    [currencies]
  )
  const baseCurrencyCode = useMemo(
    () => currencies.find((c: any) => c.is_base)?.code ?? '',
    [currencies]
  )

  // A company-wide valuation has no fund to take a functional currency from,
  // so at__save_transaction falls back to the base currency — and can only
  // populate price_functional from an explicitly-entered base-currency unit
  // value when the quote currency differs. Without it the mark still applies
  // correctly but the Transaction Summary shows no unit price, so the field
  // is required here and enforced again in at__book_transaction_checked.
  const needsBaseValuationValue = useMemo(
    () =>
      showPricePair && (allFunds || !fundId) && !!baseCurrencyId && currencyId !== baseCurrencyId,
    [showPricePair, allFunds, fundId, baseCurrencyId, currencyId]
  )

  // Functional currencies needing an explicit unit value (quote currency
  // auto-mirrors server-side) + entries orphaned by a scope/date/currency
  // change (kept visible, dropped on save). The base currency joins the list
  // when the fund-less rule above applies and no in-scope fund reports in it.
  const requiredValuationCurrencies = useMemo(() => {
    const inScope = valuationCurrencies.filter((c) => c.currency_id !== currencyId)
    if (!needsBaseValuationValue || !baseCurrencyId) return inScope
    if (inScope.some((c) => c.currency_id === baseCurrencyId)) return inScope
    const base = currencies.find((c: any) => c.id === baseCurrencyId)
    return base
      ? [...inScope, { currency_id: base.id, code: base.code, symbol: base.symbol, funds: [] }]
      : inScope
  }, [valuationCurrencies, currencyId, needsBaseValuationValue, baseCurrencyId, currencies])
  const orphanedValuationEntries = useMemo(
    () =>
      Object.entries(valuationValues).filter(
        ([ccyId, v]) =>
          v != null &&
          ccyId !== currencyId &&
          !requiredValuationCurrencies.some((c) => c.currency_id === ccyId)
      ),
    [valuationValues, currencyId, requiredValuationCurrencies]
  )

  // Company options whenever the company (or date) changes.
  const refetchCompanyOptions = useCallback(async () => {
    if (!companyId) return null
    const token = getAccessToken(store.getState().auth)
    const res = await fetchAssetTransactionCompanyOptions(companyId, transactionDate, token)
    if (res.data) setCompanyOptions(res.data)
    return res.data
  }, [companyId, transactionDate])

  useEffect(() => {
    if (!companyId) {
      setCompanyOptions(null)
      return
    }
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchAssetTransactionCompanyOptions(companyId, transactionDate, token)
      if (!cancelled && res.data) setCompanyOptions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [companyId, transactionDate])

  // "+ New security" created: refresh options, select it where it was
  // launched from, and default the currency from the new instrument.
  const handleSecurityCreated = useCallback(
    async (securityId: string) => {
      const target = addSecurityFor
      setAddSecurityFor(null)
      const opts = await refetchCompanyOptions()
      const sec =
        (target === 'new_security' ? opts?.all_securities : opts?.securities)?.find(
          (s) => s.value === securityId
        ) ?? null
      if (target === 'new_security') {
        setNewSecurityId(securityId)
      } else {
        setSecurityId(securityId)
      }
      setDirty(true)
    },
    [addSecurityFor, refetchCompanyOptions]
  )

  // Cold-load detail (screen-local; the hard-refresh path is the normal path).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Asset Transaction' }))
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchAssetTransactionDetail(String(recordId), token)
      if (cancelled) return
      if (res.error || !res.data) {
        if (res.error) showErrorToast(res.error)
        setNotFound(true)
        setLoading(false)
        return
      }
      hydrateFromDetail(res.data)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `${res.data.transaction.type_label ?? 'Transaction'} — ${res.data.transaction.company_name ?? ''}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, dispatch, formTabKey, hydrateFromDetail])

  const markDirty = () => setDirty(true)
  // Drives Save/Cancel visibility and the Book solid-vs-ghost promotion.
  const hasUnsavedChanges = isNewRecord() || dirty || journalsDirty

  // Currency never renders blank (owner spec): default from the transaction's
  // instrument context — the targeted/converting tranche's instrument, else
  // the acquired security (valuation-shaped uses_price types always take the
  // base currency) — falling back to the base currency when nothing indicates
  // one. Tracks its own last default so a user-chosen or saved currency is
  // never clobbered, while a later, more specific indicator (e.g. picking the
  // converting tranche) upgrades a mere auto-default.
  const lastCcyDefaultRef = useRef<string | null>(null)
  useEffect(() => {
    if (!showCurrency || readOnly) return
    const indicator = selectedType?.uses_price
      ? null
      : (selectedTranche?.security_currency_id ?? acquiredSecurity?.currency_id ?? null)
    const desired = indicator ?? baseCurrencyId
    if (!desired || desired === currencyId) return
    if (currencyId == null || currencyId === lastCcyDefaultRef.current) {
      lastCcyDefaultRef.current = desired
      setCurrencyId(desired)
    }
  }, [
    showCurrency,
    readOnly,
    selectedType?.uses_price,
    selectedTranche,
    acquiredSecurity,
    baseCurrencyId,
    currencyId
  ])

  // Disposed/converting quantity defaulting: a selected tranche's full held
  // position (as-of the transaction date — companyOptions refetch with the
  // date, so selectedTranche's measures already track it) pre-fills the
  // Number/Principal field. Applies when the field is empty or still holds
  // the previous auto-default, so a user-typed partial amount is never
  // clobbered by a tranche or date change.
  const lastHeldDefaultRef = useRef<{ no: number | null; principal: number | null }>({
    no: null,
    principal: null
  })
  useEffect(() => {
    if (!showDisposed || !selectedTranche || readOnly) return
    if (disposedCountable) {
      const held = selectedTranche.no_held ?? null
      setNoDisposed((prev) => {
        if (prev != null && prev !== lastHeldDefaultRef.current.no) return prev
        if (prev !== held && held != null) setDirty(true)
        lastHeldDefaultRef.current.no = held
        return held
      })
    } else {
      const held = selectedTranche.principal_held ?? null
      setPrincipalDisposed((prev) => {
        if (prev != null && prev !== lastHeldDefaultRef.current.principal) return prev
        if (prev !== held && held != null) setDirty(true)
        lastHeldDefaultRef.current.principal = held
        return held
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDisposed, disposedCountable, readOnly, selectedTranche?.value, selectedTranche?.no_held, selectedTranche?.principal_held])

  // Escrow Received default: prefill with the tranche's remaining escrow
  // balance (full release is the common case). The option's balance is
  // fund-functional, so only default when the transaction currency IS the
  // functional currency — a cross-currency prefill would be wrong. Same
  // guarded-default pattern as the held-quantity prefill above.
  const lastEscrowDefaultRef = useRef<number | null>(null)
  useEffect(() => {
    if (escrowColumn !== 'escrow_paid' || !selectedTranche || readOnly) return
    if (!currencyId || !functionalCurrencyId || currencyId !== functionalCurrencyId) return
    const balance = selectedTranche.escrow_balance ?? null
    if (balance == null || balance === 0) return
    setEscrowPaid((prev) => {
      if (prev != null && prev !== lastEscrowDefaultRef.current) return prev
      if (prev !== balance) setDirty(true)
      lastEscrowDefaultRef.current = balance
      return balance
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escrowColumn, readOnly, selectedTranche?.value, selectedTranche?.escrow_balance, currencyId, functionalCurrencyId])

  // ----- journal grid handlers -----
  const updateJournal = (key: string, patch: Partial<JournalRow>) => {
    setJournals((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    setJournalsDirty(true)
  }
  const addJournal = () => {
    setJournals((prev) => [
      ...prev,
      {
        key: nextRowKey(),
        acc_item_id: null,
        debit_credit_id: 'dr',
        amount: null,
        description: '',
        tranche_id: trancheId
      }
    ])
    setJournalsDirty(true)
  }
  const removeJournal = (key: string) => {
    setJournals((prev) => prev.filter((r) => r.key !== key))
    setJournalsDirty(true)
  }

  // ----- save / book / reset / trash -----
  // Relative-fair-value default for the basis-allocation % (owner spec):
  // FV of received securities = number received x the new security's issue
  // price; FV retained = the distributing tranche's as-of FMV (cost basis
  // when unvalued). Applies only while the field is empty or still carries
  // the previous auto-default — a typed % is never clobbered.
  const lastPercDefaultRef = useRef<number | null>(null)
  useEffect(() => {
    if (!showCostAllocation || readOnly) return
    const issuePrice = selectedNewSecurity?.issue_price ?? null
    const oldFv = selectedTranche?.fmv ?? selectedTranche?.cost_held ?? null
    if (issuePrice == null || noAcquired == null || noAcquired <= 0 || oldFv == null || oldFv <= 0) return
    const fvNew = noAcquired * issuePrice
    const suggested = Math.round((fvNew / (oldFv + fvNew)) * 10000) / 100
    setPercCostAllocated((prev) => {
      if (prev != null && prev !== lastPercDefaultRef.current) return prev
      if (prev !== suggested) setDirty(true)
      lastPercDefaultRef.current = suggested
      return suggested
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCostAllocation, readOnly, selectedNewSecurity?.issue_price, selectedTranche?.fmv, selectedTranche?.cost_held, noAcquired])

  // A type's treatments: a stale election (another type's treatment) is
  // dropped; a treatment set bound to the type (the Conversion's pair)
  // preselects its first entry on a new transaction, as the stamp of
  // 20270411120100 did for every conversion booked before it. A pattern-wide
  // set (Distribution - Securities) stays a choice the user makes.
  useEffect(() => {
    if (readOnly) return
    if (!hasTreatments) {
      if (treatmentId != null) setTreatmentId(null)
      return
    }
    if (treatmentId != null && patternTreatments.some((t: any) => t.id === treatmentId)) return
    const bound = patternTreatments.find((t: any) => t.asset_transaction_type_id != null) ?? null
    setTreatmentId(bound ? bound.id : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTreatments, patternTreatments, readOnly])

  // The gain (or loss) a Fair value election recognises and the basis the
  // received tranche starts with: the fair value less the moving cost slice,
  // slice = cost held x units converting / units held, read off the selected
  // tranche's CURRENT position (the engine reads it strictly before the
  // transaction date when it books).
  const fairValueHint = useMemo(() => {
    const fvFunctional = showFunctionalTwin ? fairValueFunctional : fairValue
    if (!showFairValue || fvFunctional == null || !selectedTranche) return null
    const held = disposedCountable ? selectedTranche.no_held : selectedTranche.principal_held
    const converting = disposedCountable ? noDisposed : principalDisposed
    const costHeld = selectedTranche.cost_held
    if (held == null || held === 0 || converting == null || costHeld == null) return null
    const slice = (costHeld * converting) / held
    const gain = fvFunctional - slice
    const topUp = additionalPayment ? ((showFunctionalTwin ? amountFunctional : amount) ?? 0) : 0
    const sym = currencySymbol(functionalCurrencyId) ?? ''
    return (
      <div className='text-xs text-gray-400 mt-1'>
        {gain < 0 ? 'Loss' : 'Gain'} recognised {sym}
        {currencyText(Math.abs(gain))} = fair value less the moving cost slice {sym}
        {currencyText(slice)} (from the tranche&apos;s current cost held and units); the received tranche&apos;s basis becomes{' '}
        {sym}
        {currencyText(fvFunctional + topUp)}
        {topUp ? ' including the additional payment' : ''}
      </div>
    )
  }, [
    showFairValue,
    showFunctionalTwin,
    fairValue,
    fairValueFunctional,
    selectedTranche,
    disposedCountable,
    noDisposed,
    principalDisposed,
    additionalPayment,
    amount,
    amountFunctional,
    functionalCurrencyId,
    currencyText
  ])

  const handleSave = useCallback(async () => {
    if (!typeId || !companyId) {
      showErrorToast('Type and company are required')
      return
    }
    if (hasTreatments && !treatmentId) {
      showErrorToast('Select an accounting treatment')
      return
    }
    if (journalsDirty) {
      const invalid = journals.find((j) => !j.acc_item_id || j.amount == null || j.amount <= 0)
      if (invalid) {
        showErrorToast('Every journal line needs an accounting item and a positive amount')
        return
      }
    }

    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: AssetTransactionSavePayload = {
        ...(detail?.transaction?.id ? { id: detail.transaction.id } : {}),
        type_id: typeId,
        company: companyId,
        name: name || null,
        ...(transactionDate ? { transaction_date: transactionDate } : {}),
        late_booking: lateBooking,
        first_report_date: lateBooking ? firstReportDate : null,
        deal: dealId,
        notes: notes || null,
        fund: fundId,
        all_funds: allFunds,
        warehoused,
        transfer_from_transaction: transferFromTransaction,
        security: securityId,
        new_security: newSecurityId,
        tranche_targeted: trancheId,
        scope_id: scopeId,
        valuation_type_id: valuationTypeId,
        security_type_id: securityTypeId,
        currency_id: currencyId,
        amount,
        amount_functional: amountFunctional,
        no_acquired: acquiredCountable ? noAcquired : null,
        principal_acquired: acquiredCountable ? null : principalAcquired,
        no_disposed: disposedCountable ? noDisposed : null,
        principal_disposed: disposedCountable ? null : principalDisposed,
        split_factor: splitFactor,
        perc_cost_allocated: showCostAllocation ? percentToDecimal(percCostAllocated) : null,
        accounting_treatment_id: hasTreatments ? treatmentId : null,
        fair_value: showFairValue ? fairValue : null,
        fair_value_functional: showFairValue ? fairValueFunctional : null,
        price,
        price_functional: priceFunctional,
        escrow_declared: escrowDeclared,
        escrow_declared_functional: escrowDeclaredFunctional,
        escrow_paid: escrowPaid,
        escrow_paid_functional: escrowPaidFunctional,
        escrow_reduced: escrowReduced,
        escrow_reduced_functional: escrowReducedFunctional,
        additional_payment: additionalPayment,
        interest_capitalized_override: showInterestOverride ? interestOverride : null,
        interest_period_end: showInterestPeriodEnd ? interestPeriodEnd : null,
        fx_rate: fxRate,
        // full replacement set: only currently-required functional
        // currencies are sent — orphaned entries drop off here
        ...(showPricePair
          ? {
              valuation_values: requiredValuationCurrencies
                .filter((c) => valuationValues[c.currency_id] != null)
                .map((c) => ({
                  currency_functional_id: c.currency_id,
                  unit_value_functional: valuationValues[c.currency_id] as number
                }))
            }
          : {}),
        ...(journalsDirty
          ? {
              journals: journals.map((j) => ({
                ...(j.id ? { id: j.id } : {}),
                acc_item_id: j.acc_item_id as string,
                debit_credit_id: j.debit_credit_id,
                amount: j.amount as number,
                description: j.description || null,
                tranche_id: j.tranche_id
              }))
            }
          : {})
      }

      const res = await saveAssetTransaction(payload, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save asset transaction')
        return
      }

      const wasNew = !detail?.transaction?.id
      hydrateFromDetail(res.data)
      toast.success(wasNew ? 'Asset transaction created' : 'Asset transaction saved')

      if (wasNew) {
        // Morph the "new" tab into the edit tab in place (no remove+re-add:
        // that left the opener tab visible for one paint — screen flash).
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_ASSET_TRANSACTIONS,
            recordId: res.data.transaction.id,
            title: `${res.data.transaction.type_label ?? 'Transaction'} — ${res.data.transaction.company_name ?? ''}`
          })
        )
        navigate(`/${TABLE_ASSET_TRANSACTIONS}/forms/full/${res.data.transaction.id}`, { replace: true })
      }
    } finally {
      setSaving(false)
    }
  }, [
    typeId,
    companyId,
    name,
    transactionDate,
    lateBooking,
    firstReportDate,
    dealId,
    notes,
    fundId,
    allFunds,
    warehoused,
    transferFromTransaction,
    securityId,
    newSecurityId,
    trancheId,
    scopeId,
    valuationTypeId,
    securityTypeId,
    currencyId,
    amount,
    amountFunctional,
    noAcquired,
    principalAcquired,
    noDisposed,
    principalDisposed,
    splitFactor,
    showCostAllocation,
    percCostAllocated,
    hasTreatments,
    treatmentId,
    showFairValue,
    fairValue,
    fairValueFunctional,
    price,
    priceFunctional,
    escrowDeclared,
    escrowDeclaredFunctional,
    escrowPaid,
    escrowPaidFunctional,
    escrowReduced,
    escrowReducedFunctional,
    additionalPayment,
    interestOverride,
    interestPeriodEnd,
    showInterestOverride,
    showInterestPeriodEnd,
    fxRate,
    showPricePair,
    requiredValuationCurrencies,
    valuationValues,
    journals,
    journalsDirty,
    acquiredCountable,
    disposedCountable,
    detail,
    dispatch,
    formTabKey,
    hydrateFromDetail,
    navigate
  ])

  const refetchDetail = useCallback(async () => {
    if (!detail?.transaction?.id) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchAssetTransactionDetail(detail.transaction.id, token)
    if (res.data) hydrateFromDetail(res.data)
  }, [detail, hydrateFromDetail])

  const handleBook = useCallback(async () => {
    if (!detail?.transaction?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await bookAssetTransaction(detail.transaction.id, token)
      if (res.error) {
        const diff = res.data?.difference
        showErrorToast(diff != null ? `${res.error} (difference: ${diff})` : res.error)
        return
      }
      const autoSec = res.data?.security_auto_create
      toast.success(
        autoSec?.security_name
          ? `Transaction booked — security "${autoSec.security_name}" ${
              autoSec.created ? 'created' : 'linked'
            } from the round`
          : 'Transaction booked'
      )
      // The security select's options predate an auto-created security —
      // refresh them so the select shows its name, not a raw id.
      if (autoSec?.security_id) await refetchCompanyOptions()
      dispatch(
        updateAssetTransactionRecord({
          transactionId: detail.transaction.id,
          updates: { cfg_transaction_statuses__label: STATUS_LABEL_BOOKED }
        })
      )
      await refetchDetail()
    } finally {
      setActionLoading(false)
    }
  }, [detail, dispatch, refetchDetail, refetchCompanyOptions])

  const handleResetJournals = useCallback(async () => {
    if (!detail?.transaction?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await resetAssetTransactionJournals(detail.transaction.id, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to reset journals')
        return
      }
      hydrateFromDetail(res.data)
      toast.success('Journals reset to engine defaults')
    } finally {
      setActionLoading(false)
    }
  }, [detail, hydrateFromDetail])

  // Unbook: same engine reversal as delete (progeny included), but the
  // transaction survives as a Draft with its detail intact.
  const handleUnbook = useCallback(async () => {
    if (!detail?.transaction?.id) return
    const token = getAccessToken(store.getState().auth)

    const dry = await unbookAssetTransaction(detail.transaction.id, token, true)
    const tranches = dry.data?.tranches_affected ?? 0
    const txs = dry.data?.transactions_affected ?? 0
    const content =
      'Unbooking unwinds the booking artefacts (deltas, journals, valuations) but keeps this transaction as a Draft.' +
      (tranches || txs
        ? ` This will also trash ${tranches} tranche(s) and ${txs} dependent transaction(s).`
        : '')

    modal.confirm({
      title: 'Unbook this asset transaction?',
      content,
      okText: 'Unbook',
      onOk: async () => {
        setActionLoading(true)
        try {
          const res = await unbookAssetTransaction(detail.transaction.id, token)
          if (res.error) {
            showErrorToast(res.error)
            return
          }
          toast.success('Transaction unbooked — back to Draft')
          await refetchDetail()
        } finally {
          setActionLoading(false)
        }
      }
    })
  }, [detail, modal, refetchDetail])

  const handleDelete = useCallback(async () => {
    if (!detail?.transaction?.id) return
    const token = getAccessToken(store.getState().auth)

    // Dry-run first: Booked transactions sweep their progeny (created
    // tranche lineage + transactions targeting it) — show the blast radius.
    let content = 'The transaction and its journal lines will be removed.'
    if (isBooked) {
      const dry = await trashAssetTransaction(detail.transaction.id, token, true)
      const tranches = dry.data?.tranches_affected ?? 0
      const txs = dry.data?.transactions_affected ?? 0
      content =
        'Trashing a booked transaction unwinds its booking artefacts (deltas, journals, valuations).' +
        (tranches || txs
          ? ` This will also trash ${tranches} tranche(s) and ${txs} dependent transaction(s).`
          : '')
    }

    modal.confirm({
      title: 'Delete this asset transaction?',
      content,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await trashAssetTransaction(detail.transaction.id, token)
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success('Asset transaction deleted')
        dispatch(removeAssetTransaction({ transactionId: detail.transaction.id }))
        navigate(LIST_PATH)
        dispatch(removeTab({ key: formTabKey }))
      }
    })
  }, [detail, isBooked, modal, dispatch, navigate, formTabKey])

  // ----- totals / booking gate -----
  const totals = useMemo(() => {
    let debits = 0
    let credits = 0
    for (const j of journals) {
      const amt = j.amount ?? 0
      if (j.debit_credit_id === 'dr') debits += amt
      else credits += amt
    }
    return { debits, credits, difference: debits - credits }
  }, [journals])

  // float tolerance: client-side summation carries FP epsilon; the server's
  // exact numeric balance check in at__book_transaction_checked stays
  // authoritative
  const isBalanced = journals.length === 0 || Math.abs(totals.difference) < 0.005
  const coaMissing =
    Boolean(detail?.transaction?.has_journal_formulas) && detail?.fund_has_coa === false
  const canBook = !isNewRecord() && !dirty && !journalsDirty && !readOnly && isBalanced && !coaMissing

  function isNewRecord() {
    return !detail?.transaction?.id
  }

  // ----- journal grid -----
  const journalColumns = [
    {
      title: 'Accounting Item',
      dataIndex: 'acc_item_id',
      width: 240,
      render: (_: unknown, row: JournalRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.acc_item_id}
          options={accItemOptions}
          placeholder='Select item...'
          showSearch
          optionFilterProp='label'
          disabled={readOnly}
          onChange={(v) => updateJournal(row.key, { acc_item_id: v })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (_: unknown, row: JournalRow) => (
        <Input
          size='small'
          value={row.description}
          disabled={readOnly}
          onChange={(e) => updateJournal(row.key, { description: e.target.value })}
        />
      )
    },
    {
      title: 'Dr / Cr',
      dataIndex: 'debit_credit_id',
      width: 110,
      render: (_: unknown, row: JournalRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.debit_credit_id}
          options={[
            { value: 'dr', label: 'Debit' },
            { value: 'cr', label: 'Credit' }
          ]}
          disabled={readOnly}
          onChange={(v) => updateJournal(row.key, { debit_credit_id: v as 'dr' | 'cr' })}
        />
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, row: JournalRow) =>
        row.debit_credit_id === 'dr' ? (
          <InputNumber
            size='small'
            className='w-full'
            value={row.amount}
            min={0.01}
            {...currencyFmt}
            disabled={readOnly}
            onChange={(v) => updateJournal(row.key, { amount: v == null ? null : Number(v) })}
          />
        ) : null
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, row: JournalRow) =>
        row.debit_credit_id === 'cr' ? (
          <InputNumber
            size='small'
            className='w-full'
            value={row.amount}
            min={0.01}
            {...currencyFmt}
            disabled={readOnly}
            onChange={(v) => updateJournal(row.key, { amount: v == null ? null : Number(v) })}
          />
        ) : null
    },
    {
      title: 'Tranche',
      dataIndex: 'tranche_name',
      width: 200,
      render: (_: unknown, row: JournalRow) =>
        row.tranche_name ? (
          <Text type='secondary' ellipsis={{ tooltip: row.tranche_name }}>
            {row.tranche_name}
          </Text>
        ) : (
          <Text type='secondary'>{row.tranche_id ? row.tranche_id : selectedType?.tranche_created ? '(new tranche)' : '-'}</Text>
        )
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: JournalRow) =>
        readOnly ? null : (
          <Button type='text' size='small' danger icon={<DeleteOutlined />} onClick={() => removeJournal(row.key)} />
        )
    }
  ]

  const journalsCustomized = Boolean(detail?.transaction?.journals_customized) || journalsDirty
  const journalExportStem = [detail?.transaction?.company_name, detail?.transaction?.type_label].filter(Boolean).join(' ')

  const journalSection = (
    <FormSection
      title='Journal Entries'
      titleColor='green'
      scrollable={false}
      headerActions={
        <>
          <ExportRowsButton
            name={journalExportStem ? `${journalExportStem} journal lines` : 'Asset transaction journal lines'}
            rows={journals}
            columns={[
              {
                title: 'Accounting Item',
                value: (r) => accItemOptions.find((o: { value: string; label: string }) => o.value === r.acc_item_id)?.label
              },
              { title: 'Description', value: (r) => r.description },
              { title: 'Dr / Cr', value: (r) => (r.debit_credit_id === 'dr' ? 'Debit' : 'Credit') },
              { title: 'Debit', value: (r) => (r.debit_credit_id === 'dr' ? r.amount : null) },
              { title: 'Credit', value: (r) => (r.debit_credit_id === 'cr' ? r.amount : null) },
              { title: 'Tranche', value: (r) => r.tranche_name ?? r.tranche_id }
            ]}
          />
          {journalsCustomized ? <Tag color='orange' className='m-0'>Customized</Tag> : <Tag className='m-0'>Engine defaults</Tag>}
          {readOnly ? null : (
            <>
              <DisabledReason reason={isNewRecord() ? null : editRight.reason}>
                <Button
                  size='small'
                  icon={<UndoOutlined />}
                  onClick={handleResetJournals}
                  disabled={isNewRecord() || actionLoading || !editRight.allowed}
                >
                  Reset to default
                </Button>
              </DisabledReason>
              <Button size='small' icon={<PlusOutlined />} onClick={addJournal} disabled={isNewRecord()}>
                Add Line
              </Button>
            </>
          )}
        </>
      }
    >
      {isNewRecord() ? (
        <Text type='secondary'>Save the transaction to generate the default journal entries.</Text>
      ) : (
        <>
          {coaMissing ? (
            <Alert
              className='mb-2'
              type='warning'
              showIcon
              message={`Fund${(detail?.funds_missing_coa?.length ?? 0) > 1 ? 's' : ''} without a chart of accounts: ${(detail?.funds_missing_coa ?? []).join(', ')} — booking is blocked until it is set up.`}
            />
          ) : null}
          {dirty ? (
            <Alert
              className='mb-2'
              type='info'
              showIcon
              message='Unsaved changes — the default journal entries refresh when you save (unless customized).'
            />
          ) : null}
          {journals.length === 0 && !detail?.transaction?.has_journal_formulas ? (
            <Text type='secondary'>
              This transaction type does not generate journal entries. Lines can still be added manually.
            </Text>
          ) : (
            <Table
              size='small'
              columns={journalColumns}
              dataSource={journals}
              pagination={false}
              rowKey='key'
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row className='font-medium'>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      Totals
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align='right'>
                      {currencyText(totals.debits)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align='right'>
                      {currencyText(totals.credits)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={2}>
                      {isBalanced ? (
                        <Tag color='green'>Balanced</Tag>
                      ) : (
                        <Tag color='gold'>Difference: {currencyText(totals.difference)}</Tag>
                      )}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          )}
        </>
      )}
    </FormSection>
  )

  // ----- field helpers -----
  // Inline layout: label left of the input (spec feedback 2026-07-24).
  const field = (label: string, control: React.ReactNode, below?: React.ReactNode) => (
    <div className='flex items-start gap-2'>
      <div className='w-32 shrink-0 text-right text-gray-500 text-sm pt-1'>{label}</div>
      <div className='flex-1 min-w-0'>
        {control}
        {below ?? null}
      </div>
    </div>
  )

  const moneyInput = (
    value: number | null,
    onChange: (v: number | null) => void,
    ccyId: string | null,
    disabled = readOnly,
    fmt = currencyFmt
  ) => (
    <InputNumber
      className='w-full'
      value={value}
      prefix={currencySymbol(ccyId) ?? ''}
      {...fmt}
      disabled={disabled}
      onChange={(v) => {
        onChange(v == null ? null : Number(v))
        markDirty()
      }}
    />
  )

  // A security Select with terms in the dropdown, a "+ New security" footer
  // and, once selected, the key terms summarized beneath the input.
  const securitySelect = (
    which: 'security' | 'new_security',
    value: string | null,
    options: AssetTransactionSecurityOption[],
    selected: AssetTransactionSecurityOption | null,
    placeholder: string,
    onPick: (v: string | null) => void
  ) => (
    <>
      <Select
        className='w-full'
        value={value}
        options={options}
        placeholder={placeholder}
        popupMatchSelectWidth={480}
        optionRender={(o) => renderSecurityOption(o.data as AssetTransactionSecurityOption)}
        showSearch
        filterOption={(input, option) =>
          securityOptionFilter(input, option as unknown as AssetTransactionSecurityOption)
        }
        disabled={readOnly}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '4px 0' }} />
            <Button
              type='text'
              block
              icon={<PlusOutlined />}
              disabled={!companyId}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setAddSecurityFor(which)}
            >
              New security…
            </Button>
          </>
        )}
        onChange={(v) => {
          onPick(v ?? null)
          markDirty()
        }}
      />
      {selected ? (
        <div className='text-xs text-gray-500 mt-1'>
          {securityTermsSummary(selected)}
          {' · '}
          <span
            className='cursor-pointer text-blue-600 hover:underline'
            onClick={() => openFormInTab('securities', selected.value, selected.label)}
          >
            Open
          </span>
        </div>
      ) : null}
    </>
  )

  // ----- common (left) column -----
  const commonFields = (
    <FormSection title='Details' titleColor='blue' scrollable={false} className='h-full'>
      <div className='flex flex-col gap-3'>
        {field(
          'Name',
          <Input
            value={name}
            disabled={readOnly}
            onChange={(e) => {
              setName(e.target.value)
              markDirty()
            }}
          />
        )}
        {field(
          'Transaction date',
          <DatePicker
            className='w-full'
            value={transactionDate ? dayjs(transactionDate) : null}
            format={DATETIME_FORMAT}
            showTime={{ format: 'HH:mm', minuteStep: 5 }}
            allowClear={false}
            disabled={readOnly}
            onChange={(d) => {
              setTransactionDate(d ? d.format(DATETIME_VALUE) : null)
              markDirty()
            }}
          />
        )}
        {field(
          'Late booking?',
          <Checkbox
            checked={lateBooking}
            disabled={readOnly}
            onChange={(e) => {
              setLateBooking(e.target.checked)
              markDirty()
            }}
          />
        )}
        {lateBooking
          ? field(
              'First report date',
              <DatePicker
                className='w-full'
                value={firstReportDate ? dayjs(firstReportDate) : null}
                format={DATE_FORMAT}
                disabled={readOnly}
                onChange={(d) => {
                  setFirstReportDate(d ? d.format('YYYY-MM-DD') : null)
                  markDirty()
                }}
              />
            )
          : null}
        {field(
          'Deal',
          <Select
            className='w-full'
            value={dealId}
            options={dealSelectOptions}
            placeholder='Select deal...'
            allowClear
            showSearch
            optionFilterProp='label'
            disabled={readOnly}
            onChange={(v) => {
              setDealId(v ?? null)
              if (v && !companyId && isNewRecord()) {
                const accountId = (headerOptions?.deals ?? []).find((d) => d.value === v)?.account_id
                if (accountId) setCompanyId(accountId)
              }
              markDirty()
            }}
          />
        )}
        {field(
          'Type',
          <Select
            className='w-full'
            value={typeId}
            options={typeOptions}
            placeholder='Select type...'
            showSearch
            optionFilterProp='label'
            disabled={readOnly}
            onChange={(v) => {
              setTypeId(v)
              markDirty()
            }}
          />
        )}
        {field(
          'Company',
          <Select
            className='w-full'
            value={companyId}
            options={companySelectOptions}
            placeholder='Select company...'
            showSearch
            optionFilterProp='label'
            disabled={readOnly || !isNewRecord()}
            onChange={(v) => {
              setCompanyId(v)
              setSecurityId(null)
              setNewSecurityId(null)
              setTrancheId(null)
              // the deal is kept: only the Deal field changes the deal
              markDirty()
            }}
          />
        )}
        {showWarehoused
          ? field(
              'Warehoused?',
              <Checkbox
                checked={warehoused}
                disabled={readOnly}
                onChange={(e) => {
                  setWarehoused(e.target.checked)
                  markDirty()
                }}
              />
            )
          : null}
        {showWarehoused && (transferSourceOptions.length > 0 || transferFromTransaction)
          ? field(
              'Transferred from',
              <Select
                className='w-full'
                value={transferFromTransaction}
                options={transferSourceOptions}
                placeholder='Not a transfer — an ordinary acquisition'
                popupMatchSelectWidth={520}
                allowClear
                showSearch
                optionFilterProp='label'
                disabled={readOnly}
                onChange={(v) => {
                  // antd's clear emits undefined: store null
                  setTransferFromTransaction(v ?? null)
                  markDirty()
                }}
              />,
              <div className='text-xs text-gray-500 mt-1'>
                A position another fund warehoused and moved here: pick that fund&apos;s Disposal, and the pair
                reads as a transfer (no new investment, follow-on or realisation inside a consolidated fund).
              </div>
            )
          : null}
        {showAllFunds
          ? field(
              'All funds?',
              <Checkbox
                checked={allFunds}
                disabled={readOnly}
                onChange={(e) => {
                  setAllFunds(e.target.checked)
                  if (e.target.checked) setFundId(null)
                  markDirty()
                }}
              />
            )
          : null}
        {showFund
          ? field(
              'Fund',
              <FundSelect
                className='w-full'
                value={fundId}
                placeholder='Select fund...'
                disabled={readOnly}
                onChange={(v) => {
                  setFundId(v ?? null)
                  markDirty()
                }}
              />
            )
          : null}
        {showScope
          ? field(
              'Scope',
              <Select
                className='w-full'
                value={scopeId}
                options={scopeOptions}
                placeholder='Select scope...'
                disabled={readOnly}
                onChange={(v) => {
                  setScopeId(v ?? null)
                  markDirty()
                }}
              />
            )
          : null}
        {showValuationTypeSelect
          ? field(
              'Valuation type',
              <Select
                className='w-full'
                value={valuationTypeId}
                options={valuationTypeOptions}
                placeholder='Select valuation type...'
                disabled={readOnly}
                onChange={(v) => {
                  setValuationTypeId(v ?? null)
                  markDirty()
                }}
              />
            )
          : null}
        {showSecurityTypeSelect
          ? field(
              'Security type',
              <Select
                className='w-full'
                value={securityTypeId}
                options={securityTypeOptions}
                placeholder='Select security type...'
                disabled={readOnly}
                onChange={(v) => {
                  setSecurityTypeId(v ?? null)
                  markDirty()
                }}
              />
            )
          : null}
        {showSecuritySelect
          ? field(
              'Security',
              securitySelect('security', securityId, securityOptions, selectedSecurity, 'Select security...', (v) => {
                setSecurityId(v)
              })
            )
          : null}
        {showSecuritySelect && securityAutoCreateLabel && !readOnly ? (
          <Alert
            type='info'
            showIcon
            className='py-1'
            message={`No security selected — a ${securityAutoCreateLabel} security will be created at booking from the round's type and terms.`}
          />
        ) : null}
        {showTrancheSelect
          ? field(
              'Tranche',
              <Select
                className='w-full'
                value={trancheId}
                options={trancheOptions}
                placeholder='Select tranche...'
                popupMatchSelectWidth={520}
                optionRender={(o) => renderTrancheOption(o.data as AssetTransactionTrancheOption)}
                showSearch
                filterOption={(input, option) =>
                  trancheOptionFilter(input, option as unknown as AssetTransactionTrancheOption)
                }
                labelRender={(o) => {
                  const t = (companyOptions?.tranches ?? []).find((x) => x.value === o.value)
                  return t ? `${t.funds__name ?? ''} — ${t.securities__name ?? ''} — ${t.date_invested_fmt ?? ''}` : o.label
                }}
                disabled={readOnly}
                onChange={(v) => {
                  setTrancheId(v ?? null)
                  markDirty()
                }}
              />
            )
          : null}
        {showTrancheSelect && selectedTranche ? (
          // Key position of the selected tranche as-of the transaction date
          // (options refetch with the date, so these figures track it).
          <div className='rounded bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600'>
            <div className='flex justify-between gap-3'>
              <span className='font-medium text-gray-700'>
                {selectedTranche.funds__name ?? '-'} — {selectedTranche.securities__name ?? '-'}
              </span>
              <span>Acquired {selectedTranche.date_invested_fmt ?? '-'}</span>
            </div>
            <div className='flex gap-4 mt-1'>
              <span>
                Held at {transactionDate ? dayjs(transactionDate).format('MM/DD/YYYY') : 'today'}:
              </span>
              {selectedTranche.is_countable ? (
                <span>
                  No.{' '}
                  {selectedTranche.no_held != null
                    ? Number(selectedTranche.no_held).toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : '-'}
                </span>
              ) : (
                <span>
                  Principal{' '}
                  {selectedTranche.principal_held != null
                    ? `${selectedTranche.security_currency_symbol ?? ''}${Number(
                        selectedTranche.principal_held
                      ).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : '-'}
                </span>
              )}
              <span>
                Cost{' '}
                {selectedTranche.cost_held != null
                  ? `${selectedTranche.currency_symbol ?? ''}${Number(
                      selectedTranche.cost_held
                    ).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                  : '-'}
              </span>
              {(selectedTranche.escrow_balance ?? 0) !== 0 ? (
                <span>
                  Escrow{' '}
                  {`${selectedTranche.currency_symbol ?? ''}${Number(
                    selectedTranche.escrow_balance
                  ).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </FormSection>
  )

  // ----- type-specific (right) column -----
  const amountLabel = selectedType?.amount_label ?? 'Amount'

  const typeFields = (
    <FormSection title={selectedType ? selectedType.label : 'Transaction'} titleColor='purple' scrollable={false} className='h-full'>
      {!selectedType ? (
        <Text type='secondary'>Select a transaction type to see its fields.</Text>
      ) : (
        <div className='flex flex-col gap-3'>
          {showDisposed
            ? field(
                disposedCountable ? `Number ${disposedNoun}` : `Principal ${disposedNoun}`,
                disposedCountable ? (
                  <InputNumber
                    className='w-full'
                    value={noDisposed}
                    {...unitCountFmt}
                    disabled={readOnly}
                    onChange={(v) => {
                      setNoDisposed(v == null ? null : Number(v))
                      markDirty()
                    }}
                  />
                ) : (
                  moneyInput(principalDisposed, setPrincipalDisposed, selectedTranche?.security_currency_id ?? null)
                )
              )
            : null}

          {showInterestPeriodEnd
            ? field(
                'Accrued to',
                <DatePicker
                  className='w-full'
                  value={interestPeriodEnd ? dayjs(interestPeriodEnd) : null}
                  format={DATE_FORMAT}
                  disabled={readOnly}
                  onChange={(d) => {
                    setInterestPeriodEnd(d ? d.format('YYYY-MM-DD') : null)
                    markDirty()
                  }}
                />,
                <div className='text-xs text-gray-400 mt-1'>The period end this accrual is &quot;to&quot;</div>
              )
            : null}

          {showNewSecurity
            ? field(
                'Security received',
                securitySelect(
                  'new_security',
                  newSecurityId,
                  allSecurityOptions,
                  selectedNewSecurity,
                  'Select security (any company)...',
                  (v) => setNewSecurityId(v)
                )
              )
            : null}

          {showAcquired
            ? field(
                acquiredCountable ? `Number ${acquiredNoun}` : `Principal ${acquiredNoun}`,
                acquiredCountable ? (
                  <InputNumber
                    className='w-full'
                    value={noAcquired}
                    {...unitCountFmt}
                    disabled={readOnly}
                    onChange={(v) => {
                      setNoAcquired(v == null ? null : Number(v))
                      markDirty()
                    }}
                  />
                ) : (
                  moneyInput(principalAcquired, setPrincipalAcquired, acquiredSecurity?.currency_id ?? null)
                )
              )
            : null}

          {showAdditionalPayment
            ? field(
                'Additional payment?',
                <Checkbox
                  checked={additionalPayment}
                  disabled={readOnly}
                  onChange={(e) => {
                    setAdditionalPayment(e.target.checked)
                    markDirty()
                  }}
                />
              )
            : null}

          {showCurrency
            ? field(
                'Currency',
                <div className='max-w-40'>
                  <Select
                    className='w-full'
                    value={currencyId}
                    options={currencyOptions}
                    placeholder='Currency...'
                    showSearch
                    optionFilterProp='label'
                    disabled={readOnly}
                    onChange={(v) => {
                      setCurrencyId(v ?? null)
                      markDirty()
                    }}
                  />
                </div>
              )
            : null}

          {hasTreatments
            ? field(
                'Accounting treatment',
                <Select
                  className='w-full'
                  value={treatmentId}
                  options={patternTreatments.map((t: any) => ({ value: t.id, label: t.label }))}
                  placeholder='Select treatment...'
                  disabled={readOnly}
                  onChange={(v) => {
                    setTreatmentId(v ?? null)
                    const t = patternTreatments.find((x: any) => x.id === v)
                    // the non-applicable value input clears with the switch
                    if (treatmentsUsePercent && !t?.uses_percent) setPercCostAllocated(null)
                    if (treatmentsUseAmount && !t?.uses_amount) {
                      setAmount(null)
                      setAmountFunctional(null)
                    }
                    if (!t?.uses_fair_value) {
                      setFairValue(null)
                      setFairValueFunctional(null)
                    }
                    markDirty()
                  }}
                />
              )
            : null}
          {showFairValue
            ? field(
                'Fair value received',
                moneyInput(fairValue, setFairValue, currencyId),
                showFunctionalTwin ? null : fairValueHint
              )
            : null}
          {showFairValue && showFunctionalTwin
            ? field(
                'Fair value received (functional)',
                moneyInput(fairValueFunctional, setFairValueFunctional, functionalCurrencyId),
                <>
                  {feedHint}
                  {fairValueHint}
                </>
              )
            : null}
          {showAmountInput ? field(amountLabel, moneyInput(amount, setAmount, currencyId)) : null}
          {showAmountInput && showFunctionalTwin
            ? field(
                `${amountLabel} (functional)`,
                moneyInput(amountFunctional, setAmountFunctional, functionalCurrencyId),
                feedHint
              )
            : null}

          {showPricePair ? field('Unit value', moneyInput(price, setPrice, currencyId, readOnly, unitPriceFmt)) : null}
          {showPricePair && showFunctionalTwin
            ? field(
                'Unit value (functional)',
                moneyInput(priceFunctional, setPriceFunctional, functionalCurrencyId, readOnly, unitPriceFmt)
              )
            : null}
          {/* Explicit unit values per in-scope functional currency that
              differs from the quote currency — one asset_valuations row per
              currency at booking; the quote currency auto-mirrors. */}
          {showPricePair && requiredValuationCurrencies.length > 0 ? (
            <>
              <Text type='secondary' className='text-xs'>
                Enter the unit value in{' '}
                {requiredValuationCurrencies.map((c) => c.code).join(', ')} — one valuation is
                recorded per currency.
              </Text>
              {needsBaseValuationValue ? (
                <Alert
                  type='info'
                  showIcon
                  className='py-1'
                  message={`This valuation specifies no fund, so it reports in ${baseCurrencyCode} (the base currency). The ${baseCurrencyCode} unit value is required — without it the transaction summary shows no unit price.`}
                />
              ) : null}
              {requiredValuationCurrencies.map((c) =>
                field(
                  `Unit value (${c.code})${
                    needsBaseValuationValue && c.currency_id === baseCurrencyId ? ' *' : ''
                  }`,
                  <InputNumber
                    key={c.currency_id}
                    className='w-full'
                    value={valuationValues[c.currency_id] ?? null}
                    prefix={c.symbol ?? ''}
                    {...unitPriceFmt}
                    disabled={readOnly}
                    onChange={(v) => {
                      setValuationValues((prev) => ({
                        ...prev,
                        [c.currency_id]: v == null ? null : Number(v)
                      }))
                      markDirty()
                    }}
                  />
                )
              )}
            </>
          ) : null}
          {showPricePair && orphanedValuationEntries.length > 0 ? (
            <Alert
              type='warning'
              showIcon
              className='py-1'
              message={`Unit values no longer in scope (dropped on save): ${orphanedValuationEntries
                .map(
                  ([ccyId, v]) =>
                    `${currencies.find((c: any) => c.id === ccyId)?.code ?? 'currency'} ${v}`
                )
                .join(', ')}`}
            />
          ) : null}

          {showCostAllocation ? (
            <>
              {field(
                'Cost allocated %',
                <InputNumber
                  className='w-full'
                  value={percCostAllocated}
                  suffix='%'
                  min={0}
                  max={100}
                  disabled={readOnly}
                  onChange={(v) => {
                    setPercCostAllocated(v == null ? null : Number(v))
                    markDirty()
                  }}
                />
              )}
              <Text type='secondary' className='text-xs'>
                Basis carve-out: this % of the distributing tranche&apos;s cost moves to the received
                tranche. Defaulted from relative fair values (number received × the new
                security&apos;s issue price vs the distributing tranche&apos;s fair value).
              </Text>
            </>
          ) : null}
          {showSplitFactor
            ? field(
                'Split factor',
                <InputNumber
                  className='w-full'
                  value={splitFactor}
                  {...unitCountFmt}
                  disabled={readOnly}
                  onChange={(v) => {
                    setSplitFactor(v == null ? null : Number(v))
                    markDirty()
                  }}
                />
              )
            : null}

          {escrowColumn === 'escrow_declared'
            ? field(
                showAmountPair ? 'Escrow held back' : 'Escrow declared',
                moneyInput(escrowDeclared, setEscrowDeclared, currencyId)
              )
            : null}
          {escrowColumn === 'escrow_declared' && showFunctionalTwin
            ? field(
                'Escrow held back (functional)',
                moneyInput(escrowDeclaredFunctional, setEscrowDeclaredFunctional, functionalCurrencyId),
                feedHint
              )
            : null}
          {escrowColumn === 'escrow_paid'
            ? field('Escrow received', moneyInput(escrowPaid, setEscrowPaid, currencyId))
            : null}
          {escrowColumn === 'escrow_paid' && showFunctionalTwin
            ? field('Escrow received (functional)', moneyInput(escrowPaidFunctional, setEscrowPaidFunctional, functionalCurrencyId), feedHint)
            : null}
          {escrowColumn === 'escrow_reduced'
            ? field('Escrow reduced', moneyInput(escrowReduced, setEscrowReduced, currencyId))
            : null}
          {escrowColumn === 'escrow_reduced' && showFunctionalTwin
            ? field(
                'Escrow reduced (functional)',
                moneyInput(escrowReducedFunctional, setEscrowReducedFunctional, functionalCurrencyId),
                feedHint
              )
            : null}

          {showFxRate
            ? field(
                'FX rate',
                <InputNumber
                  className='w-full'
                  value={fxRate}
                  {...fxRateFmt}
                  disabled={readOnly}
                  onChange={(v) => {
                    setFxRate(v == null ? null : Number(v))
                    markDirty()
                  }}
                />,
                <>
                  <div className='text-xs text-gray-400 mt-1'>Security currency → functional currency</div>
                  {fxRateFromFeed ? <div className='text-xs text-gray-400'>Rate filled from the FX feed</div> : null}
                </>
              )
            : null}

          {field(
            'Notes',
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              value={notes}
              disabled={readOnly}
              onChange={(e) => {
                setNotes(e.target.value)
                markDirty()
              }}
            />
          )}
        </div>
      )}
    </FormSection>
  )

  // ----- Accrued interest (docs/INTEREST_ACCRUAL_DESIGN.md 6.2 / 6.3) -----
  // The detail payload carries the block only when the saved transaction
  // targets a tranche whose security has interest terms; the figures are the
  // engine's for the SAVED row, so an edited override or amount shows its
  // effect after Save.
  const interest = detail?.interest ?? null
  const interestSymbol = interest?.security_currency_symbol ?? ''
  const interestMoney = (v: number | null | undefined): string =>
    v == null ? '—' : `${interestSymbol}${currencyText(v)}`
  const savedUnitsDisposed =
    detail?.transaction?.no_disposed ?? detail?.transaction?.principal_disposed ?? null
  const interestShare =
    interest?.units_held_before && savedUnitsDisposed != null
      ? savedUnitsDisposed / interest.units_held_before
      : null
  const thisAccrual =
    interest?.calculated_to_date != null
      ? Math.round((interest.calculated_to_date - (interest.booked_earned ?? 0)) * 100) / 100
      : null
  const amountIsOverride =
    showInterestPeriodEnd && thisAccrual != null && amount != null && Math.round(amount * 100) / 100 !== thisAccrual
  // the receipt / write-off the exit guard asks for: a new transaction on
  // the same tranche, one minute before this one (the escrow split's
  // convention); the two types are named by their cfg labels
  const openInterestSettlement = (typeLabel: string) => {
    const t = assetTypes.find((x: any) => x.label === typeLabel)
    if (!t || !companyId || !trancheId) return
    const d = transactionDate ? dayjs(transactionDate).subtract(1, 'minute') : dayjs()
    openFormInTab(TABLE_ASSET_TRANSACTIONS, null, `New ${typeLabel}`, {
      company: companyId,
      tranche: trancheId,
      type_id: String(t.id),
      date: d.format(DATETIME_VALUE)
    })
  }
  const interestTerms = interest
    ? [
        interest.interest_rate != null ? `${(interest.interest_rate * 100).toFixed(2)}%` : null,
        interest.interest_rate_type_label ?? 'Simple',
        interest.compounds && interest.compounding_frequency_label ? interest.compounding_frequency_label : null,
        interest.day_count_label ?? null,
        interest.interest_accrues_from ? `accrues from ${dayjs(interest.interest_accrues_from).format(DATE_FORMAT)}` : null,
        interest.maturity_date
          ? `matures ${dayjs(interest.maturity_date).format(DATE_FORMAT)}${interest.accrues_after_maturity === false ? ' (stops there)' : ''}`
          : null
      ]
        .filter(Boolean)
        .join(' · ')
    : ''
  const interestSection =
    interest && !isNewRecord() ? (
      <FormSection title='Accrued interest' titleColor='orange' scrollable={false}>
        <div className='flex flex-col gap-3'>
          {field(
            'Tranche',
            <Text>
              {interest.tranche_name ?? '—'}
              {interestShare != null && showInterestOverride ? (
                <Text type='secondary' className='ml-2'>{(interestShare * 100).toFixed(1)}% of units converted</Text>
              ) : null}
            </Text>
          )}
          {field('Terms', <Text type='secondary'>{interestTerms || 'No interest terms'}</Text>)}
          {showInterestPeriodEnd ? (
            <>
              {field('Accrued to date (calc.)', <Text>{interestMoney(interest.calculated_to_date)}</Text>,
                <div className='text-xs text-gray-400 mt-1'>The engine at the close of the transaction date</div>)}
              {field('Booked before', <Text>{interestMoney(interest.booked_earned)}</Text>,
                <div className='text-xs text-gray-400 mt-1'>Interest earned on earlier accruals, net of write-offs</div>)}
              {field(
                'This accrual',
                <Space>
                  <Text strong={!amountIsOverride} delete={amountIsOverride} type={amountIsOverride ? 'secondary' : undefined}>
                    {interestMoney(thisAccrual)}
                  </Text>
                  {amountIsOverride ? <Tag color='gold'>Override</Tag> : null}
                  {!readOnly && thisAccrual != null && amount !== thisAccrual ? (
                    <Button
                      size='small'
                      onClick={() => {
                        setAmount(thisAccrual)
                        if (currencyId && functionalCurrencyId && currencyId === functionalCurrencyId) {
                          setAmountFunctional(thisAccrual)
                        }
                        markDirty()
                      }}
                    >
                      Use calculated
                    </Button>
                  ) : null}
                </Space>,
                <div className='text-xs text-gray-400 mt-1'>Calculated less booked before; the amount above is what books</div>
              )}
            </>
          ) : showInterestOverride ? (
            <>
              {field(
                'Booked balance',
                <Text>
                  {interestMoney(interestShare != null && interest.booked_balance != null
                    ? Math.round(interest.booked_balance * interestShare * 100) / 100
                    : interest.booked_balance)}
                </Text>,
                <div className='text-xs text-gray-400 mt-1'>The converted slice of the balance earlier accruals booked</div>
              )}
              {field(
                'Calculated at conversion',
                <Text>
                  {interestMoney(interestShare != null && interest.calculated_before != null
                    ? Math.round(interest.calculated_before * interestShare * 100) / 100
                    : interest.calculated_before)}
                </Text>,
                <div className='text-xs text-gray-400 mt-1'>The engine up to the conversion date, on the converted slice</div>
              )}
              {field(
                'Override',
                <InputNumber
                  className='w-full'
                  aria-label='Capitalised interest override'
                  value={interestOverride}
                  prefix={interestSymbol}
                  {...currencyFmt}
                  status={interestOverride != null ? 'warning' : undefined}
                  disabled={readOnly}
                  onChange={(v) => {
                    setInterestOverride(v == null ? null : Number(v))
                    markDirty()
                  }}
                />,
                <div className='text-xs text-gray-400 mt-1'>
                  Empty = the calculated figure is capitalised; clearing the override is the undo
                  {dirty ? ' · Save to see the capitalised figure follow it' : ''}
                </div>
              )}
              {field('Capitalised', <Text strong>{interestMoney(interest.capitalized)}</Text>,
                <div className='text-xs text-gray-400 mt-1'>Moves into the new tranche&apos;s cost (Dr Investment / Cr Accrued interest receivable)</div>)}
              {field(
                'True-up',
                <Text type={interest.trueup != null && interest.trueup < 0 ? 'danger' : undefined}>
                  {interestMoney(interest.trueup)}
                </Text>,
                <div className='text-xs text-gray-400 mt-1'>Accrued (or reversed) by this conversion to reach the capitalised figure</div>
              )}
            </>
          ) : (
            <>
              {field('Open balance', <Text strong>{interestMoney(interest.booked_balance)}</Text>,
                <div className='text-xs text-gray-400 mt-1'>Accrued interest booked on the tranche before this transaction</div>)}
              {showDisposed && (interest.booked_balance ?? 0) > 0 ? (
                <Alert
                  type='warning'
                  showIcon
                  className='py-1'
                  message='A full exit is refused while accrued interest is open - book the receipt or the write-off first (one minute before this transaction).'
                  action={
                    <Space>
                      <DisabledReason reason={createRight.reason}>
                        <Button
                          size='small'
                          disabled={!createRight.allowed}
                          onClick={() => openInterestSettlement('Interest Received')}
                        >
                          Receive in cash…
                        </Button>
                      </DisabledReason>
                      <DisabledReason reason={createRight.reason}>
                        <Button
                          size='small'
                          disabled={!createRight.allowed}
                          onClick={() => openInterestSettlement('Interest Written Off')}
                        >
                          Write off…
                        </Button>
                      </DisabledReason>
                    </Space>
                  }
                />
              ) : null}
            </>
          )}
        </div>
      </FormSection>
    ) : null

  const detailsContent = (
    <div className='flex flex-col gap-3'>
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={9} xl={8}>
          {commonFields}
        </Col>
        <Col xs={24} lg={15} xl={16}>
          <div className='flex flex-col gap-3'>
            {typeFields}
            {interestSection}
          </div>
        </Col>
      </Row>
      {!journalsOnSeparateTab ? journalSection : null}
    </div>
  )

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className='p-10 text-center text-gray-500'>
        Asset transaction not found.
        <div className='mt-3'>
          <Button onClick={() => navigate(LIST_PATH)}>Back to Asset Transactions</Button>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full w-full min-h-0 flex flex-col bg-gray-50 p-3 overflow-hidden'>
      {modalContextHolder}
      <AddSecurityModal
        open={addSecurityFor !== null}
        companyId={companyId}
        companyName={companySelectOptions.find((c) => c.value === companyId)?.label ?? null}
        // Currency default (owner spec): conversions ('new_security', the
        // security received) inherit the converting-out security's currency
        // — the targeted tranche's; subscriptions/purchases inherit the
        // transaction currency. The modal falls back to the base currency.
        defaultCurrencyId={
          addSecurityFor === 'new_security'
            ? selectedTranche?.security_currency_id ?? currencyId
            : currencyId
        }
        onCreated={handleSecurityCreated}
        onCancel={() => setAddSecurityFor(null)}
      />
      <Card
        size='small'
        className='flex-1 min-h-0 flex flex-col h-full overflow-hidden'
        classNames={{ body: 'h-full p-3 overflow-y-auto' }}
      >
        <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
          <Space align='center'>
            <Title level={5} className='!mb-0'>
              {isNewRecord()
                ? 'New Asset Transaction'
                : `${detail?.transaction.type_label ?? 'Transaction'} — ${detail?.transaction.company_name ?? ''}`}
            </Title>
            {detail?.transaction?.status_label ? (
              <Tag color={isBooked ? 'green' : 'blue'}>{detail.transaction.status_label}</Tag>
            ) : null}
          </Space>
          <Space>
            {/* Fixed button grammar (owner spec): [Delete] [dismiss] [primary].
                Slots never move; only occupants change with the workflow step:
                Create (new) -> Save (dirty) -> Book (clean draft) -> Close
                (booked). Book is a FUTURE step while unsaved changes exist,
                so it is hidden then — not shown disabled. */}
            {!isNewRecord() ? (
              <DisabledReason reason={deleteRight.reason}>
                <Button danger disabled={!deleteRight.allowed} onClick={handleDelete}>
                  Delete
                </Button>
              </DisabledReason>
            ) : null}
            {isBooked ? (
              <DisabledReason reason={unbookRight.reason}>
                <Button loading={actionLoading} disabled={!unbookRight.allowed} onClick={handleUnbook}>
                  Unbook
                </Button>
              </DisabledReason>
            ) : null}
            {/* a Booked realization (a type whose amount is Proceeds) can be
                distributed: the distribution form opens pre-scoped to the
                fund with this realization as its first source
                (docs/WATERFALL_CARRY_DESIGN.md, phase 3) */}
            {isBooked && selectedType?.amount_label === 'Proceeds' && fundId && detail?.transaction?.id ? (
              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  openFormInTab(TABLE_DISTRIBUTIONS, null, 'New Distribution', {
                    fund: fundId,
                    asset_transaction: detail.transaction.id
                  })
                }
              >
                Distribution
              </Button>
            ) : null}
            <Button onClick={() => closeFormTab(formTabKey)}>
              {!readOnly && hasUnsavedChanges ? 'Cancel' : 'Close'}
            </Button>
            {!readOnly && hasUnsavedChanges ? (
              <DisabledReason reason={isNewRecord() ? createRight.reason : editRight.reason}>
                <Button
                  type='primary'
                  loading={saving}
                  disabled={!(isNewRecord() ? createRight.allowed : editRight.allowed)}
                  onClick={handleSave}
                >
                  {isNewRecord() ? 'Create' : 'Save'}
                </Button>
              </DisabledReason>
            ) : null}
            {!isNewRecord() && !isBooked && !hasUnsavedChanges ? (
              <DisabledReason reason={bookRight.reason}>
                <Button
                  type='primary'
                  disabled={!canBook || !bookRight.allowed}
                  loading={actionLoading}
                  onClick={handleBook}
                >
                  Book
                </Button>
              </DisabledReason>
            ) : null}
          </Space>
        </div>

        {journalsOnSeparateTab ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: 'details', label: 'Details', children: detailsContent },
              { key: 'journals', label: 'Journals', children: journalSection }
            ]}
          />
        ) : (
          detailsContent
        )}
      </Card>
    </div>
  )
}
